/**
 * Storage Service
 * Firebase Storage operations for image uploads
 */

import {
  ref,
  uploadBytes,
  uploadBytesResumable,
  getDownloadURL,
  deleteObject,
  type UploadTask,
  type UploadTaskSnapshot,
} from 'firebase/storage'
import { storage } from '../config/firebase'
import { sanitizeFilename, validateImageFile } from '../utils/sanitize'

// ============================================================================
// Types
// ============================================================================

export interface UploadProgress {
  progress: number
  bytesTransferred: number
  totalBytes: number
  state: 'running' | 'paused' | 'success' | 'canceled' | 'error'
}

export interface UploadResult {
  url: string
  path: string
  filename: string
}

export interface UploadOptions {
  maxSizeMB?: number
  folder?: string
  onProgress?: (progress: UploadProgress) => void
}

// ============================================================================
// Image Upload
// ============================================================================

/**
 * Upload an image file with progress tracking
 */
export async function uploadImage(file: File, options: UploadOptions = {}): Promise<UploadResult> {
  const { maxSizeMB = 10, folder = 'reports', onProgress } = options

  // Validate file
  const validation = validateImageFile(file, maxSizeMB)
  if (!validation.valid) {
    throw new Error(validation.error)
  }

  // Generate unique filename
  const timestamp = Date.now()
  const randomSuffix = Math.random().toString(36).substring(2, 8)
  const sanitizedName = sanitizeFilename(file.name)
  const extension = getFileExtension(sanitizedName)
  const filename = `${timestamp}-${randomSuffix}${extension}`
  const path = `${folder}/${filename}`

  // Create storage reference
  const storageRef = ref(storage, path)

  // Upload with progress tracking
  if (onProgress) {
    return uploadWithProgress(storageRef, file, path, filename, onProgress)
  }

  // Simple upload without progress
  await uploadBytes(storageRef, file, {
    contentType: file.type,
    customMetadata: {
      originalName: file.name,
      uploadedAt: new Date().toISOString(),
    },
  })

  const url = await getDownloadURL(storageRef)

  return { url, path, filename }
}

/**
 * Upload with progress tracking
 */
async function uploadWithProgress(
  storageRef: ReturnType<typeof ref>,
  file: File,
  path: string,
  filename: string,
  onProgress: (progress: UploadProgress) => void
): Promise<UploadResult> {
  return new Promise((resolve, reject) => {
    const uploadTask: UploadTask = uploadBytesResumable(storageRef, file, {
      contentType: file.type,
      customMetadata: {
        originalName: file.name,
        uploadedAt: new Date().toISOString(),
      },
    })

    uploadTask.on(
      'state_changed',
      (snapshot: UploadTaskSnapshot) => {
        const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100

        onProgress({
          progress,
          bytesTransferred: snapshot.bytesTransferred,
          totalBytes: snapshot.totalBytes,
          state: snapshot.state as UploadProgress['state'],
        })
      },
      (error) => {
        reject(new Error(`Upload failed: ${error.message}`))
      },
      async () => {
        try {
          const url = await getDownloadURL(uploadTask.snapshot.ref)
          resolve({ url, path, filename })
        } catch (error) {
          reject(error)
        }
      }
    )
  })
}

/**
 * Upload multiple images
 */
export async function uploadImages(
  files: File[],
  options: UploadOptions = {}
): Promise<UploadResult[]> {
  const results: UploadResult[] = []

  for (const file of files) {
    const result = await uploadImage(file, options)
    results.push(result)
  }

  return results
}

// ============================================================================
// Image Deletion
// ============================================================================

/**
 * Delete an image by path
 */
export async function deleteImage(path: string): Promise<void> {
  const storageRef = ref(storage, path)
  await deleteObject(storageRef)
}

/**
 * Delete an image by URL
 */
export async function deleteImageByUrl(url: string): Promise<void> {
  // Extract path from Firebase Storage URL
  const path = extractPathFromUrl(url)
  if (path) {
    await deleteImage(path)
  }
}

// ============================================================================
// Image URL Utilities
// ============================================================================

/**
 * Get download URL for a path
 */
export async function getImageUrl(path: string): Promise<string> {
  const storageRef = ref(storage, path)
  return getDownloadURL(storageRef)
}

/**
 * Check if a URL is a Firebase Storage URL
 */
export function isFirebaseStorageUrl(url: string): boolean {
  return url.includes('firebasestorage.googleapis.com') || url.includes('storage.googleapis.com')
}

/**
 * Extract storage path from Firebase Storage URL
 */
function extractPathFromUrl(url: string): string | null {
  try {
    const urlObj = new URL(url)
    const pathMatch = /\/o\/(.+?)(?:\?|$)/.exec(urlObj.pathname)

    if (pathMatch?.[1]) {
      return decodeURIComponent(pathMatch[1])
    }

    return null
  } catch {
    return null
  }
}

// ============================================================================
// Image Processing Utilities
// ============================================================================

/**
 * Compress image before upload
 */
export async function compressImage(
  file: File,
  maxWidth = 1920,
  maxHeight = 1080,
  quality = 0.8
): Promise<File> {
  return new Promise((resolve, reject) => {
    const img = new Image()
    const canvas = document.createElement('canvas')
    const ctx = canvas.getContext('2d')

    if (!ctx) {
      reject(new Error('Could not get canvas context'))
      return
    }

    img.onload = () => {
      let { width, height } = img

      // Calculate new dimensions
      if (width > maxWidth) {
        height = (height * maxWidth) / width
        width = maxWidth
      }

      if (height > maxHeight) {
        width = (width * maxHeight) / height
        height = maxHeight
      }

      canvas.width = width
      canvas.height = height

      // Draw and compress
      ctx.drawImage(img, 0, 0, width, height)

      canvas.toBlob(
        (blob) => {
          if (!blob) {
            reject(new Error('Could not compress image'))
            return
          }

          const compressedFile = new File([blob], file.name, {
            type: 'image/jpeg',
            lastModified: Date.now(),
          })

          resolve(compressedFile)
        },
        'image/jpeg',
        quality
      )
    }

    img.onerror = () => {
      reject(new Error('Could not load image'))
    }
    img.src = URL.createObjectURL(file)
  })
}

/**
 * Create image thumbnail
 */
export async function createThumbnail(file: File, size = 200): Promise<File> {
  return compressImage(file, size, size, 0.7)
}

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * Get file extension from filename
 */
function getFileExtension(filename: string): string {
  const lastDot = filename.lastIndexOf('.')
  if (lastDot === -1) {
    return ''
  }
  return filename.substring(lastDot).toLowerCase()
}

/**
 * Generate a preview URL for a file
 */
export function createPreviewUrl(file: File): string {
  return URL.createObjectURL(file)
}

/**
 * Revoke a preview URL to free memory
 */
export function revokePreviewUrl(url: string): void {
  URL.revokeObjectURL(url)
}
